import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useResponsive } from 'contexts/ResponsiveContext';
import { StorageE } from 'declarations/enums/storage';
import type { WipListFiltersI, WipRecordI } from 'declarations/interfaces/wip';
import { useFetchWipRecordsInfinite } from 'hooks/api/useWip';
import OpenNewTicketPopup from 'pages/TicketsPage/components/OpenNewTicketPopup/OpenNewTicketPopup';
import {
  selectWipAppliedFilters,
  selectWipPage,
  selectWipSearchTerm,
  selectWipSelectedIds,
  selectWipSelectedRoofTopId,
  selectWipSorting,
} from 'reduxStore/features/wip/wipSlice';
import { useAppSelector } from 'reduxStore/hooks';

import DealerCard from './components/DealerCard/DealerCard';
import Header from './components/Header/Header';
import WIPChirpTicketAbortDialog from './components/WIPChirpTicketAbortDialog/WIPChirpTicketAbortDialog';
import WIPColumnPickerPopup from './components/WIPColumnPickerPopup/WIPColumnPickerPopup';
import WIPItemsTable from './components/WIPTable/WIPItemsTable';
import { getDefaultVisibleWipColumns } from './components/WIPTable/WIPItemsTableConfiguration';
import { useWipChirpTicketQueue } from './hooks/useWipChirpTicketQueue';
import {
  CardsRowCollapsibleStyled,
  CardsRowLayoutWrapperStyled,
  CardsRowSectionStyled,
  ContentWrapStyled,
  DealerCardWrapStyled,
  HeaderBoxStyled,
  TableContentWrapStyled,
  WIPPageStyled,
} from './WIPPage.styled';

const WIPPage: FC = (): JSX.Element => {
  const { isMobile } = useResponsive();

  const { pageSize } = useAppSelector(selectWipPage);
  const sorting = useAppSelector(selectWipSorting);
  const selectedRoofTopId = useAppSelector(selectWipSelectedRoofTopId);
  const searchTerm = useAppSelector(selectWipSearchTerm);
  const appliedFilters = useAppSelector(selectWipAppliedFilters);

  const [isCardsRowExpanded, setIsCardsRowExpanded] = useState<boolean>((): boolean => {
    try {
      const stored = localStorage.getItem(StorageE.WipCardsRowExpanded);
      if (stored !== null) return stored === 'true';
    } catch {
      // Ignore storage errors
    }
    return !isMobile;
  });

  useEffect((): void => {
    try {
      localStorage.setItem(StorageE.WipCardsRowExpanded, String(isCardsRowExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isCardsRowExpanded]);

  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState<boolean>(false);

  const handleOpenColumnPicker = useCallback((): void => {
    setIsColumnPickerOpen(true);
  }, []);

  const handleCloseColumnPicker = useCallback((): void => {
    setIsColumnPickerOpen(false);
  }, []);

  const visibleColumns = useMemo(getDefaultVisibleWipColumns, []);

  const requestFilter: WipListFiltersI = useMemo((): WipListFiltersI => {
    // Dealer refinement on the dealer card takes precedence when set; otherwise we apply the
    // multi-select dealer filter from the filters dropdown.
    let roofTopIds: string[] | undefined;
    if (selectedRoofTopId) {
      roofTopIds = [selectedRoofTopId];
    } else if (appliedFilters.roofTopIds.length > 0) {
      roofTopIds = appliedFilters.roofTopIds;
    } else {
      roofTopIds = undefined;
    }

    return {
      roofTopIds,
      searchTerm: searchTerm || undefined,
      oemFamilyIds:
        appliedFilters.oemFamilyIds.length > 0 ? appliedFilters.oemFamilyIds : undefined,
      wipStatuses: appliedFilters.wipStatuses.length > 0 ? appliedFilters.wipStatuses : undefined,
      claimStatuses:
        appliedFilters.claimStatuses.length > 0 ? appliedFilters.claimStatuses : undefined,
      repairOrderStatuses:
        appliedFilters.repairOrderStatuses.length > 0
          ? appliedFilters.repairOrderStatuses
          : undefined,
      backlog: appliedFilters.backlog,
      createdAtFrom: appliedFilters.createdAtFrom,
      createdAtTo: appliedFilters.createdAtTo,
    };
  }, [
    appliedFilters.backlog,
    appliedFilters.claimStatuses,
    appliedFilters.createdAtFrom,
    appliedFilters.createdAtTo,
    appliedFilters.oemFamilyIds,
    appliedFilters.repairOrderStatuses,
    appliedFilters.roofTopIds,
    appliedFilters.wipStatuses,
    searchTerm,
    selectedRoofTopId,
  ]);

  const infiniteRequest = useMemo(
    () => ({
      pageSize,
      sorting: {
        parameter: sorting.parameter,
        order: sorting.order,
      },
      filter: requestFilter,
    }),
    [pageSize, sorting, requestFilter]
  );

  const {
    data: wipData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchWipRecordsInfinite(infiniteRequest);

  const wipPages = wipData?.pages;
  const totalCount = wipPages?.[0]?.totalCount ?? 0;

  const handleFetchNextPage = useCallback((): void => {
    fetchNextPage();
  }, [fetchNextPage]);

  /**
   * Build the selection set used by the Create Chirp toolbar. Mirrors WarrQueue: the queue is
   * driven off the currently visible records that the user has checked, in the order they appear
   * on the page (the WIP slice stores IDs; we resolve them against the loaded pages so we always
   * carry the most up-to-date record fields).
   */
  const selectedWipIds = useAppSelector(selectWipSelectedIds);
  const wipItemsSelected = useMemo<WipRecordI[]>((): WipRecordI[] => {
    if (selectedWipIds.length === 0 || !wipPages) return [];
    const selectedSet = new Set<string>(selectedWipIds);
    const records: WipRecordI[] = [];
    for (const page of wipPages) {
      for (const record of page.data) {
        if (record.wipId && selectedSet.has(record.wipId)) {
          records.push(record);
        }
      }
    }
    return records;
  }, [selectedWipIds, wipPages]);

  const chirpTicketQueue = useWipChirpTicketQueue();
  const handleCreateChirpTicketClick = useCallback((): void => {
    if (wipItemsSelected.length === 0) return;
    chirpTicketQueue.startQueue(wipItemsSelected);
  }, [chirpTicketQueue, wipItemsSelected]);

  const remainingChirpCount =
    chirpTicketQueue.sessionTotal > 0
      ? Math.max(chirpTicketQueue.sessionTotal - chirpTicketQueue.currentPosition + 1, 0)
      : 0;

  return (
    <WIPPageStyled>
      <ContentWrapStyled>
        <HeaderBoxStyled>
          <CardsRowSectionStyled $expanded={isCardsRowExpanded}>
            <CardsRowCollapsibleStyled $expanded={isCardsRowExpanded}>
              <CardsRowLayoutWrapperStyled>
                <DealerCardWrapStyled>
                  <DealerCard />
                </DealerCardWrapStyled>
              </CardsRowLayoutWrapperStyled>
            </CardsRowCollapsibleStyled>
          </CardsRowSectionStyled>
          <Header
            isCardsRowExpanded={isCardsRowExpanded}
            onToggleCardsRow={(): void => setIsCardsRowExpanded((prev): boolean => !prev)}
            onOpenColumnPicker={handleOpenColumnPicker}
            wipItemsSelected={wipItemsSelected}
            onCreateChirpTicketClick={handleCreateChirpTicketClick}
          />
        </HeaderBoxStyled>
        <TableContentWrapStyled>
          <WIPItemsTable
            wipPages={wipPages}
            totalCount={totalCount}
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            fetchNextPage={handleFetchNextPage}
            visibleColumns={visibleColumns}
          />
        </TableContentWrapStyled>
      </ContentWrapStyled>
      <WIPColumnPickerPopup isOpen={isColumnPickerOpen} onClose={handleCloseColumnPicker} />
      <OpenNewTicketPopup />
      <WIPChirpTicketAbortDialog
        open={chirpTicketQueue.isAbortConfirmOpen}
        remainingCount={remainingChirpCount}
        onCancel={chirpTicketQueue.cancelAbort}
        onConfirm={chirpTicketQueue.confirmAbort}
      />
    </WIPPageStyled>
  );
};

export default WIPPage;
